import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const ArticleAdd: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    status: 'published',
    category: '',
    cultivation_stage: 'کشت',
    tags: '',
    keywords: '',
    is_featured: false
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch('https://fellfell.ir/api/upload-article.php', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        return data.filename;
      } else {
        throw new Error(data.message || 'خطا در آپلود تصویر');
      }
    } catch (error) {
      throw new Error('خطا در آپلود تصویر: ' + (error as Error).message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      showToast('عنوان و محتوای مقاله الزامی است', 'error');
      return;
    }

    setSaving(true);

    try {
      let imageName = '';
      if (imageFile) {
        try {
          imageName = await uploadImage(imageFile);
        } catch (uploadError) {
          showToast((uploadError as Error).message, 'error');
          setSaving(false);
          return;
        }
      }

      const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
      const keywordsArray = formData.keywords.split(',').map(kw => kw.trim()).filter(kw => kw !== '');

      const payload = {
        title: formData.title,
        summary: formData.summary,
        content: formData.content,
        status: formData.status,
        category: formData.category,
        cultivation_stage: formData.cultivation_stage,
        tags: tagsArray,
        keywords: keywordsArray,
        is_featured: formData.is_featured ? 1 : 0,
        image: imageName || null
      };

      console.log('📤 ارسال مقاله:', payload);

      const response = await fetch('https://fellfell.ir/api/articles.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('📨 پاسخ:', data);

      if (data.success) {
        showToast('✅ مقاله با موفقیت اضافه شد', 'success');
        setTimeout(() => {
          navigate('/admin/articles');
        }, 1500);
      } else {
        showToast('❌ خطا در ذخیره مقاله: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      console.error('❌ خطا:', err);
      showToast('❌ خطا در ذخیره مقاله: ' + err.message, 'error');
    }

    setSaving(false);
  };

  return (
    <div style={{ direction: 'rtl', padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '14px 28px',
          borderRadius: '12px',
          fontSize: '15px',
          zIndex: 9999,
          background: toast.type === 'success' ? '#2e7d32' : '#c62828',
          color: '#fff',
          boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <h1 style={{ color: '#1e3a1e', fontSize: '24px' }}>✍️ افزودن مقاله جدید</h1>
        <Link to="/admin/articles" style={{
          padding: '8px 16px',
          background: '#e0e0e0',
          color: '#333',
          textDecoration: 'none',
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          ← بازگشت به لیست مقالات
        </Link>
      </div>

      <form onSubmit={handleSubmit} style={{ background: '#fff', padding: '30px', borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
        {/* عنوان */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
            عنوان مقاله <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
            placeholder="عنوان مقاله را وارد کنید"
            required
          />
        </div>

        {/* خلاصه */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
            خلاصه مقاله
          </label>
          <input
            type="text"
            value={formData.summary}
            onChange={e => setFormData({ ...formData, summary: e.target.value })}
            style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
            placeholder="خلاصه کوتاه از مقاله"
          />
        </div>

        {/* محتوا */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
            محتوای مقاله <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <textarea
            value={formData.content}
            onChange={e => setFormData({ ...formData, content: e.target.value })}
            rows={10}
            style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px', resize: 'vertical' }}
            placeholder="متن کامل مقاله را وارد کنید (می‌توانید از HTML استفاده کنید)..."
            required
          />
        </div>

        {/* دسته‌بندی و مرحله کشت */}
        <div style={{ display: 'flex', gap: '15px', marginBottom: '18px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              دسته‌بندی
            </label>
            <select
              value={formData.category}
              onChange={e => setFormData({ ...formData, category: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
            >
              <option value="">بدون دسته‌بندی</option>
              <option value="کشاورزی">🌾 کشاورزی</option>
              <option value="دامپروری">🐄 دامپروری</option>
              <option value="باغبانی">🌳 باغبانی</option>
              <option value="آبیاری">💧 آبیاری</option>
              <option value="تغذیه">🍎 تغذیه</option>
              <option value="بازاریابی">📊 بازاریابی</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              مرحله کشت <span style={{ color: '#d32f2f' }}>*</span>
            </label>
            <select
              value={formData.cultivation_stage}
              onChange={e => setFormData({ ...formData, cultivation_stage: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
              required
            >
              <option value="کشت">🌱 کشت (کاشت)</option>
              <option value="داشت">🌿 داشت (داشت و رشد)</option>
              <option value="برداشت">🌾 برداشت</option>
              <option value="نگهداشت">🏺 نگهداشت (نگهداری و انبارداری)</option>
            </select>
          </div>
        </div>

        {/* وضعیت */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
            وضعیت
          </label>
          <select
            value={formData.status}
            onChange={e => setFormData({ ...formData, status: e.target.value })}
            style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
          >
            <option value="published">منتشر شده</option>
            <option value="draft">پیش‌نویس</option>
          </select>
        </div>

        {/* برچسب‌ها و کلمات کلیدی */}
        <div style={{ display: 'flex', gap: '15px', marginBottom: '18px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              🏷️ برچسب‌ها
              <span style={{ fontSize: '12px', color: '#888', marginRight: '5px' }}>(با کاما جدا کنید)</span>
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={e => setFormData({ ...formData, tags: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
              placeholder="مثال: کشاورزی, ارگانیک, گلخانه"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              🔑 کلمات کلیدی
              <span style={{ fontSize: '12px', color: '#888', marginRight: '5px' }}>(با کاما جدا کنید)</span>
            </label>
            <input
              type="text"
              value={formData.keywords}
              onChange={e => setFormData({ ...formData, keywords: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
              placeholder="مثال: محصولات ارگانیک, کشت گلخانه, آبیاری قطره‌ای"
            />
          </div>
        </div>

        {/* تصویر */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
            تصویر مقاله
          </label>
          <div style={{
            border: '2px dashed #ddd',
            borderRadius: '10px',
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
            background: '#fafafa'
          }}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#2e7d32'; }}
          onDragLeave={(e) => { e.currentTarget.style.borderColor = '#ddd'; }}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) {
              setImageFile(file);
              const reader = new FileReader();
              reader.onloadend = () => setImagePreview(reader.result as string);
              reader.readAsDataURL(file);
            }
          }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              id="image-upload"
            />
            <label htmlFor="image-upload" style={{ cursor: 'pointer', display: 'block' }}>
              {imagePreview ? (
                <img src={imagePreview} alt="پیش‌نمایش" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }} />
              ) : (
                <>
                  <div style={{ fontSize: '40px', marginBottom: '10px' }}>🖼️</div>
                  <p style={{ color: '#888' }}>برای آپلود تصویر کلیک کنید یا بکشید</p>
                  <p style={{ fontSize: '12px', color: '#aaa' }}>فرمت‌های مجاز: JPG, PNG, GIF</p>
                </>
              )}
            </label>
          </div>
          {imageFile && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#555' }}>
              📎 {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
              <button
                type="button"
                onClick={() => { setImageFile(null); setImagePreview(''); }}
                style={{ marginRight: '10px', background: 'none', border: 'none', color: '#c62828', cursor: 'pointer' }}
              >
                ❌ حذف
              </button>
            </div>
          )}
        </div>

        {/* ویژه */}
        <div style={{ marginBottom: '22px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={e => setFormData({ ...formData, is_featured: e.target.checked })}
              style={{ width: '18px', height: '18px' }}
            />
            <span>⭐ مقاله ویژه (نمایش در صفحه اصلی)</span>
          </label>
        </div>

        {/* دکمه‌ها */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <Link to="/admin/articles" style={{
            padding: '12px 24px',
            background: '#e0e0e0',
            color: '#333',
            textDecoration: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '500'
          }}>
            انصراف
          </Link>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 30px',
              background: '#2e7d32',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? '⏳ در حال ذخیره...' : '➕ افزودن مقاله'}
          </button>
        </div>
      </form>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ArticleAdd;